
import React, { useState, useEffect, useCallback } from 'react';
import { PersonFinal } from '@/api/entities';
import { AccountFinal } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/use-toast';
import { EyeOff, AlertTriangle, Loader2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

export default function OrphanedPeopleCleanup() {
  const [orphanedPeople, setOrphanedPeople] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const { toast } = useToast();

  const loadOrphanedPeople = useCallback(async () => {
    setIsLoading(true);
    try {
      const [allPeople, allAccounts] = await Promise.all([
        PersonFinal.list(),
        AccountFinal.list()
      ]);

      // Create a set of all existing account names for quick lookup
      const existingAccountNames = new Set(
        allAccounts.map(acc => acc.account_name?.trim().toLowerCase()).filter(Boolean)
      );

      // Find people whose account_name doesn't match any existing account
      const orphaned = allPeople.filter(person => {
        if (!person.account_name) return true; // No account name at all
        
        const normalizedAccountName = person.account_name.trim().toLowerCase();
        return !existingAccountNames.has(normalizedAccountName) && person.is_visible !== false;
      });

      // Group by account name for easier review
      const groupedOrphans = orphaned.reduce((acc, person) => {
        const accountName = person.account_name || 'No Account Name';
        if (!acc[accountName]) {
          acc[accountName] = [];
        }
        acc[accountName].push(person);
        return acc;
      }, {});

      setOrphanedPeople(orphaned);
      console.log('Orphaned people grouped by account:', groupedOrphans);
    } catch (error) {
      console.error('Error loading orphaned people:', error);
      toast({ variant: 'destructive', title: 'Failed to load data' });
    }
    setIsLoading(false);
  }, [toast]); // Added toast to dependency array

  useEffect(() => {
    loadOrphanedPeople();
  }, [loadOrphanedPeople]); // Added loadOrphanedPeople to dependency array

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(orphanedPeople.map(p => p.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id, checked) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkHide = async () => {
    if (selectedIds.size === 0) {
      toast({ title: 'No records selected', variant: 'destructive' });
      return;
    }

    if (!confirm(`Are you sure you want to hide ${selectedIds.size} orphaned person records?`)) {
      return;
    }

    setIsUpdating(true);
    let successCount = 0;
    const errors = [];

    // Process one by one to avoid rate limiting
    for (const id of selectedIds) {
      try {
        const person = await PersonFinal.get(id);
        await PersonFinal.update(id, { ...person, is_visible: false });
        successCount++;
      } catch (error) {
        console.error(`Failed to update person ${id}:`, error);
        errors.push(id);
      }
    }

    if (successCount > 0) {
      toast({ title: `Successfully hid ${successCount} person records` });
    }

    if (errors.length > 0) {
      toast({ 
        variant: 'destructive',
        title: `Failed to update ${errors.length} records`,
        description: 'Some records could not be updated. Please try again.'
      });
    }

    setSelectedIds(new Set());
    await loadOrphanedPeople(); // Reload to see updated data
    setIsUpdating(false);
  };

  const getAccountGroups = () => {
    const groups = {};
    orphanedPeople.forEach(person => {
      const accountName = person.account_name || 'No Account Name';
      if (!groups[accountName]) {
        groups[accountName] = [];
      }
      groups[accountName].push(person);
    });
    return groups;
  };

  // This variable was not used, removed it to avoid linting warnings
  // const isAllSelected = orphanedPeople.length > 0 && selectedIds.size === orphanedPeople.length;

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const accountGroups = getAccountGroups();

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#0A1A2F]">Orphaned People Cleanup</h1>
          <p className="text-lg text-[#5C6B7A]">People records without corresponding account records</p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p>Found <strong>{orphanedPeople.length}</strong> person records that don't have matching account records.</p>
            <p className="text-sm text-gray-600 mt-2">
              These are grouped by account name below. You can select records to hide them from the application.
            </p>
          </CardContent>
        </Card>

        {orphanedPeople.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-green-600">No orphaned person records found! All people have corresponding accounts.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="flex items-center gap-4 mb-4">
              <Button 
                onClick={handleBulkHide}
                disabled={isUpdating || selectedIds.size === 0}
                variant="destructive"
              >
                {isUpdating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <EyeOff className="mr-2 h-4 w-4" />}
                Hide Selected ({selectedIds.size})
              </Button>
            </div>

            <div className="space-y-6">
              {Object.entries(accountGroups).map(([accountName, people]) => (
                <Card key={accountName}>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {accountName} ({people.length} people)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-96">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">
                              <Checkbox
                                checked={people.every(p => selectedIds.has(p.id))}
                                onCheckedChange={(checked) => {
                                  const newSelected = new Set(selectedIds);
                                  people.forEach(p => {
                                    if (checked) {
                                      newSelected.add(p.id);
                                    } else {
                                      newSelected.delete(p.id);
                                    }
                                  });
                                  setSelectedIds(newSelected);
                                }}
                              />
                            </TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead>Department</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {people.map(person => (
                            <TableRow key={person.id} data-state={selectedIds.has(person.id) && "selected"}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedIds.has(person.id)}
                                  onCheckedChange={(checked) => handleSelectOne(person.id, checked)}
                                />
                              </TableCell>
                              <TableCell>
                                {person.first_name} {person.last_name}
                              </TableCell>
                              <TableCell>{person.role_title || '-'}</TableCell>
                              <TableCell>{person.department || '-'}</TableCell>
                              <TableCell>
                                <Badge variant="destructive">No Account Match</Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
